import { Component, inject, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { OtpVerifyBody } from '~app/common/models/otp-verify-body';
import { OtpVerifyResponse } from '~app/common/models/otp-verify-response';
import { Status } from '~app/common/models/response';
import { SendOtpResponse } from '~app/common/models/send-otp-response';
import { SentOtpBody } from '~app/common/models/sent-otp-body';
import { UiUtilityService } from '~app/common/services/ui-utility/ui-utility.service';
import { environment } from '~environments/environment';
import { navigationRoutes } from '../../routes/navigation/navigation-routes';
import { OtpVerificationService } from './otp-verification.service';

declare global {
    interface Window {
        grecaptcha?: {
            ready(arg0: () => void): unknown;
            render: (containerId: string, options: { sitekey: string; callback: (token: string) => void }) => void;
        } & Partial<{
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        }>;
    }
}

@Component({
    selector: 'app-otp-verification',
    standalone: false,
    templateUrl: './otp-verification.component.html',
    styleUrl: './otp-verification.component.scss',
})
export class OtpVerificationComponent implements OnInit, OnDestroy {
    otpVerificationForm: FormGroup;
    navigationRoutes = navigationRoutes;
    returnUrl!: string;
    isResendDisabled = false;
    remainingTime!: number;
    resendIntervalSeconds = 1;
    timerSubscription!: Subscription;
    otpPurpose!:
        | 'login'
        | 'resetPassword'
        | 'new-user-registration'
        | 'sendCustomerLogin'
        | 'sendCustomerRegistration'
        | 'sendCustomerResetPassword';
    flowfor?: string;
    captchaToken: string | null = null;
    isCaptchaRequired = false;
    isCaptchaRendered = false; // NEW field

    @Input() flowFrom?: string;

    private readonly fb = inject(FormBuilder);
    private readonly otpService = inject(OtpVerificationService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly zone = inject(NgZone);
    private readonly router = inject(Router);
    private readonly uiUtilityService = inject(UiUtilityService);

    constructor() {
        window.addEventListener('captcha-success', (event: Event) => {
            this.captchaToken = (event as CustomEvent).detail;
        });

        this.otpVerificationForm = this.fb.group({
            otp: ['', Validators.required],
        });
    }

    onCaptchaResolved(token: string) {
        this.captchaToken = token;
    }

    ngOnInit(): void {
        this.otpPurpose = sessionStorage.getItem('otp-purpose') as
            | 'login'
            | 'resetPassword'
            | 'new-user-registration'
            | 'sendCustomerLogin'
            | 'sendCustomerRegistration'
            | 'sendCustomerResetPassword';
        this.returnUrl = this.activatedRoute.snapshot.queryParams['returnUrl'] || '/sendtestapp';
        this.flowfor = this.activatedRoute.snapshot.queryParams['flowFor'];

        if (this.flowfor === 'login' && this.flowFrom !== 'sendCustomer' && this.flowFrom !== 'admin') {
            this.processLogin();
        } else {
            this.startTimer();
        }

        // harcoded for now
        this.flowfor = 'emailOtp';
    }

    startTimer(): void {
        this.timerSubscription?.unsubscribe();
        this.remainingTime = this.resendIntervalSeconds;
        this.isResendDisabled = true;
        this.timerSubscription = interval(1000).subscribe(() => {
            this.zone.run(() => {
                if (--this.remainingTime <= 0) {
                    this.timerSubscription.unsubscribe();
                    this.isResendDisabled = false;
                }
            });
        });
    }

    getFormattedTime(seconds: number): string {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        const formattedMinutes = String(minutes).padStart(2, '0');
        const formattedSeconds = String(remainingSeconds).padStart(2, '0');
        return `${formattedMinutes}:${formattedSeconds}`;
    }

    resendOTP() {
        const sendOtpBody: SentOtpBody = {
            email: sessionStorage.getItem('email-id'),
            id: sessionStorage.getItem('otp-user-id'),
            event: sessionStorage.getItem('otp-event'),
            captchaToken: null,
        };

        // If captcha is required, send captchaToken also
        if (this.isCaptchaRequired && this.captchaToken) {
            sendOtpBody.captchaToken = this.captchaToken;
        }

        if (this.isCaptchaRequired && !this.captchaToken) {
            // If captcha is required but not selected, show alert and prevent submit
            this.uiUtilityService.handleError('Please complete the captcha before submitting.');
            return;
        }

        this.otpService.resendOtp(sendOtpBody).subscribe({
            next: (res: SendOtpResponse) => {
                if (res?.status === Status.OK) {
                    this.isCaptchaRequired = false;
                    this.isCaptchaRendered = false;
                    this.startTimer();
                } else if (res?.captchaRequired) {
                    this.isCaptchaRequired = true;
                    this.isResendDisabled = true;
                    this.executeCaptcha();
                } else if (res?.tooManyRequests) {
                    this.isCaptchaRequired = false;
                    this.isCaptchaRendered = false;
                    this.isResendDisabled = true;
                }
            },
            error: () => {
                this.isResendDisabled = true;
            },
        });
    }

    private executeCaptcha(): void {
        const grecaptcha = window['grecaptcha'];

        if (grecaptcha?.ready && typeof grecaptcha.ready === 'function') {
            grecaptcha.ready(() => {
                grecaptcha.execute?.(environment.googleSiteKey, { action: 'otp_verification' })?.then((token: string) => {
                    this.zone.run(() => {
                        this.captchaToken = token;
                        this.isResendDisabled = false;
                        this.startTimer(); // resume timer
                    });
                });
            });

            this.isCaptchaRendered = true;
        } else {
            console.error('reCAPTCHA is not available.');
        }
    }

    submitForm(): void {
        if (this.isCaptchaRequired && !this.captchaToken) {
            // If captcha is required but not selected, show alert and prevent submit
            this.uiUtilityService.handleError('Please complete the captcha before submitting.');
            return;
        }

        const otpVerifyBody: OtpVerifyBody = {
            id: sessionStorage.getItem('otp-user-id'),
            event: sessionStorage.getItem('otp-event'),
            email: sessionStorage.getItem('otp-email'),
            tempOtp: this.otpVerificationForm.value.otp,
            mfaMethod: 'email',
            captchaToken: this.captchaToken, // pass token here
        };
        if (this.otpVerificationForm.valid) {
            this.otpService.verifyOtp(otpVerifyBody, this.flowFrom).subscribe((res: OtpVerifyResponse) => {
                if (res.status === Status.OK && res.verificationStatus) {
                    this.processLogin();
                }
            });
        }
    }

    processLogin(): void {
        if (this.flowFrom === 'sendCustomer') {
            console.log('this.otpPurpose 1', this.otpPurpose);
            localStorage.setItem('login-flow', 'sendCustomer');
            this.otpService.processLogin(this.otpPurpose, this.returnUrl ?? '/returnUrl');
        }

        if (this.flowFrom === 'admin') {
            console.log('this.otpPurpose 1', this.otpPurpose);
            localStorage.setItem('login-flow', 'admin');
            this.otpService.processLogin(this.otpPurpose, this.returnUrl ?? '/admin-login');
        }
    }

    backFromOtpVerificationScreen(): void {
        if (this.flowFrom === 'admin') {
            this.router.navigate([navigationRoutes.navigateToAdminLogin]);
            return;
        }
        this.router.navigate([navigationRoutes.navigateToSendCustomerLogin]);
    }

    ngOnDestroy() {
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
        }
    }
}
